import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getImageUrl } from '../lib/api';
import { resolveFilterCSSSync, preloadFilters } from '../lib/filterUtils';
import {
  Heart, Smile, Clock, Star,
  MapPin, Users, Sparkles,
  BookOpen, ChevronRight, PenLine,
  Share2, Bookmark, Sunrise, CalendarDays, EyeOff, Camera,
  TrendingUp
} from 'lucide-react';

// Ensure filter cache is warm
preloadFilters();

const REACTIONS = [
  { key: 'love', icon: Heart, label: 'Love', color: 'text-rose-500 bg-rose-50' },
  { key: 'joy', icon: Smile, label: 'Joy', color: 'text-amber-500 bg-amber-50' },
  { key: 'nostalgia', icon: Clock, label: 'Nostalgia', color: 'text-indigo-500 bg-indigo-50' },
  { key: 'epic', icon: Star, label: 'Epic', color: 'text-tan-500 bg-tan-50' },
];

const cardVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.55,
      delay: i * 0.08,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  })
};

// ─── SHELL ──────────────────────────────────────────────
function CardShell({ index, children, className = '', testId }) {
  return (
    <motion.article
      custom={index}
      initial="hidden"
      animate="visible"
      variants={cardVariants}
      className={`feed-card ${className}`}
      data-testid={testId}
    >
      {children}
    </motion.article>
  );
}

// ─── MEMORY CARD ────────────────────────────────────────
function MemoryCard({ data, onNavigate }) {
  const [showReactions, setShowReactions] = useState(false);
  const [reacted, setReacted] = useState(null);
  const imgSrc = getImageUrl(data.thumbnail_url || data.preview_url || data.url);
  const people = data.people || [];
  const filterStyle = resolveFilterCSSSync(data.filter_applied, data.filter_intensity);

  return (
    <>
      <button
        onClick={() => onNavigate(`/memories/${data.memory_id}`)}
        className="block w-full relative aspect-[4/3] overflow-hidden bg-[#1C1C1C]"
        data-testid={`feed-image-${data.memory_id}`}
      >
        <img src={imgSrc} alt={data.title || 'Memory'}
          className="w-full h-full object-cover" loading="lazy"
          style={filterStyle ? { filter: filterStyle.cssFilter } : undefined}
        />
        {filterStyle?.overlay && (
          <div className="absolute inset-0 pointer-events-none" style={{ background: filterStyle.overlay }} />
        )}
        <div className="absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
        <div className="absolute bottom-3.5 left-4 right-4 flex items-end justify-between">
          <div>
            {data.title && (
              <h3 className="text-white font-heading font-semibold text-sm leading-tight drop-shadow-md">{data.title}</h3>
            )}
            <div className="flex items-center gap-2.5 mt-1">
              {data.date && (
                <span className="text-white/75 text-xs flex items-center gap-1 font-body">
                  <Clock className="w-3 h-3" />
                  {new Date(data.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
              )}
              {data.location && (
                <span className="text-white/75 text-xs flex items-center gap-1 font-body">
                  <MapPin className="w-3 h-3" />
                  {data.location}
                </span>
              )}
            </div>
          </div>
          {people.length > 0 && (
            <div className="flex items-center gap-1 bg-[#141414]/15 backdrop-blur-md rounded-full px-2.5 py-1">
              <Users className="w-3 h-3 text-white" />
              <span className="text-white text-xs font-body font-medium">{people.length}</span>
            </div>
          )}
        </div>
      </button>

      <div className="px-4 py-3.5">
        {data.ai_caption && (
          <p className="text-stone-400 text-sm leading-relaxed line-clamp-2 mb-3 font-body">{data.ai_caption}</p>
        )}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-0.5 relative">
            <button
              onClick={() => setShowReactions(!showReactions)}
              className={`p-2 rounded-full transition-colors ${reacted ? REACTIONS.find(r => r.key === reacted)?.color : 'text-stone-400 hover:bg-[#0F0F0F]'}`}
              data-testid={`feed-react-${data.memory_id}`}
            >
              {reacted
                ? React.createElement(REACTIONS.find(r => r.key === reacted)?.icon || Heart, { className: 'w-[18px] h-[18px]' })
                : <Heart className="w-[18px] h-[18px]" />}
            </button>
            <AnimatePresence>
              {showReactions && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.85, y: 6 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.85, y: 6 }}
                  transition={{ duration: 0.15 }}
                  className="absolute bottom-full left-0 mb-2 flex gap-1 bg-[#141414] rounded-full px-2 py-1.5 shadow-xl border border-[#1C1C1C]"
                >
                  {REACTIONS.map(r => (
                    <button
                      key={r.key}
                      onClick={() => { setReacted(r.key); setShowReactions(false); }}
                      className={`p-1.5 rounded-full transition-all hover:scale-110 ${r.color}`}
                      title={r.label}
                    >
                      <r.icon className="w-4 h-4" />
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
            <button
              className="p-2 rounded-full text-stone-400 hover:bg-[#0F0F0F] transition-colors"
              onClick={() => onNavigate(`/memories/${data.memory_id}`)}
              data-testid={`feed-journal-${data.memory_id}`}
            >
              <PenLine className="w-[18px] h-[18px]" />
            </button>
            <button
              className="p-2 rounded-full text-stone-400 hover:bg-[#0F0F0F] transition-colors"
              data-testid={`feed-share-${data.memory_id}`}
            >
              <Share2 className="w-[18px] h-[18px]" />
            </button>
          </div>
          <button
            className="p-2 rounded-full text-stone-400 hover:bg-[#0F0F0F] transition-colors"
            data-testid={`feed-bookmark-${data.memory_id}`}
          >
            <Bookmark className="w-[18px] h-[18px]" />
          </button>
        </div>
      </div>
    </>
  );
}

// ─── ON THIS DAY CARD ───────────────────────────────────
function OnThisDayCard({ data, onNavigate }) {
  const memories = data.memories || [];
  if (memories.length === 0) return null;

  return (
    <>
      <div className="px-4 pt-4 pb-2 flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-primary/8 flex items-center justify-center">
          <Clock className="w-4 h-4 text-primary" />
        </div>
        <div>
          <h3 className="font-heading font-semibold text-white text-sm">{data.label}</h3>
          <p className="text-stone-400 text-xs font-body">{data.memory_count} memories from this day</p>
        </div>
      </div>
      <div className="px-4 pb-4 overflow-x-auto scrollbar-hide">
        <div className="flex gap-2.5" style={{ minWidth: 'max-content' }}>
          {memories.map((mem, idx) => (
            <button
              key={mem.memory_id || idx}
              onClick={() => onNavigate(`/memories/${mem.memory_id}`)}
              className="w-28 flex-shrink-0 rounded-lg overflow-hidden group"
            >
              <div className="aspect-square bg-[#1C1C1C] overflow-hidden rounded-lg">
                <img
                  src={getImageUrl(mem.thumbnail_url || mem.preview_url || mem.url)}
                  alt={mem.title || ''}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
              </div>
              {mem.title && (
                <p className="text-xs text-stone-300 font-body font-medium mt-1.5 px-0.5 truncate">{mem.title}</p>
              )}
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

// ─── AI HIGHLIGHT CARD ──────────────────────────────────
function AIHighlightCard({ data, onNavigate }) {
  const imgSrc = getImageUrl(data.preview_url || data.url || data.thumbnail_url);

  return (
    <>
      <div className="px-4 pt-3.5 pb-2 flex items-center gap-2.5">
        <div className="w-7 h-7 rounded-full bg-amber-50 flex items-center justify-center">
          <Sparkles className="w-3.5 h-3.5 text-amber-600" />
        </div>
        <span className="text-[11px] font-body font-semibold text-amber-700 uppercase tracking-widest">AI Pick</span>
      </div>
      <button
        onClick={() => onNavigate(`/memories/${data.memory_id}`)}
        className="block w-full relative aspect-[4/3] overflow-hidden bg-[#1C1C1C]"
      >
        <img src={imgSrc} alt={data.title || ''} className="w-full h-full object-cover" loading="lazy" />
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/50 to-transparent" />
        <div className="absolute bottom-3.5 left-4">
          {data.title && <h3 className="text-white font-heading font-semibold text-sm drop-shadow-md">{data.title}</h3>}
          {data.date && (
            <p className="text-white/70 text-xs mt-0.5 font-body">
              {new Date(data.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </p>
          )}
        </div>
      </button>
      <div className="px-4 py-3.5">
        <p className="text-stone-400 text-xs italic font-body">{data.reason}</p>
        {data.ai_caption && <p className="text-stone-400 text-sm mt-2 line-clamp-2 font-body">{data.ai_caption}</p>}
      </div>
    </>
  );
}

// ─── LIFE CHAPTER CARD ──────────────────────────────────
function LifeChapterCard({ data, onNavigate }) {
  return (
    <button
      onClick={() => onNavigate(`/life-chapters/${data.chapter_id}`)}
      className="w-full text-left p-5"
    >
      <div className="flex items-center gap-2.5 mb-3">
        <div className="w-9 h-9 rounded-full bg-primary/8 flex items-center justify-center">
          <BookOpen className="w-4 h-4 text-primary" />
        </div>
        <span className="text-[11px] font-body font-semibold text-primary uppercase tracking-widest">Life Chapter</span>
      </div>
      <h3 className="font-heading font-bold text-white text-lg leading-snug">{data.title}</h3>
      {data.description && (
        <p className="text-stone-400 text-sm mt-1.5 line-clamp-2 font-body leading-relaxed">{data.description}</p>
      )}
      <div className="flex items-center gap-3 mt-3.5">
        {data.start_date && (
          <span className="text-stone-400 text-xs font-body">
            {new Date(data.start_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
          </span>
        )}
        {data.memory_count > 0 && <span className="text-stone-400 text-xs font-body">{data.memory_count} memories</span>}
        <ChevronRight className="w-4 h-4 text-stone-300 ml-auto" />
      </div>
    </button>
  );
}

// ─── PEOPLE MEMORY CARD ─────────────────────────────────
function PeopleMemoryCard({ data, onNavigate }) {
  const photos = data.recent_photos || [];
  if (photos.length === 0) return null;

  return (
    <>
      <button
        onClick={() => onNavigate(`/people/${data.person_id}`)}
        className="w-full text-left px-4 pt-4 pb-2"
        data-testid={`feed-people-${data.person_id}`}
      >
        <div className="flex items-center gap-3 mb-3">
          <div className="w-11 h-11 rounded-full overflow-hidden bg-gradient-to-br from-rose-100 to-purple-100 flex-shrink-0">
            {data.cover_photo_url ? (
              <img src={getImageUrl(data.cover_photo_url)} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Users className="w-5 h-5 text-stone-300" />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading font-semibold text-white text-sm truncate">{data.person_name}</h3>
            <p className="text-stone-400 text-xs font-body">
              {data.photo_count} {data.photo_count === 1 ? 'memory' : 'memories'} together
              {data.relationship && <span className="ml-1 capitalize">· {data.relationship}</span>}
            </p>
          </div>
          <ChevronRight className="w-4 h-4 text-stone-300 flex-shrink-0" />
        </div>
      </button>
      <div className="px-4 pb-4 overflow-x-auto scrollbar-hide">
        <div className="flex gap-2" style={{ minWidth: 'max-content' }}>
          {photos.map((p, i) => (
            <button
              key={p.memory_id || i}
              onClick={() => onNavigate(`/memories/${p.memory_id}`)}
              className="w-24 flex-shrink-0 rounded-lg overflow-hidden group"
            >
              <div className="aspect-square bg-[#1C1C1C] overflow-hidden rounded-lg">
                <img
                  src={getImageUrl(p.thumbnail_url || p.preview_url || p.url)}
                  alt={p.title || ''}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
              </div>
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

// ─── LOCATION MEMORY CARD ───────────────────────────────
function LocationMemoryCard({ data, onNavigate }) {
  const photos = data.recent_photos || [];
  if (photos.length === 0) return null;

  return (
    <>
      <div className="px-4 pt-4 pb-2">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-9 h-9 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
            <MapPin className="w-4 h-4 text-blue-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading font-semibold text-white text-sm truncate">{data.location}</h3>
            <p className="text-stone-400 text-xs font-body">
              {data.photo_count} {data.photo_count === 1 ? 'memory' : 'memories'}
            </p>
          </div>
        </div>
      </div>
      <div className="px-4 pb-4">
        <div className="grid grid-cols-3 gap-1.5 rounded-xl overflow-hidden">
          {photos.slice(0, 6).map((p, i) => (
            <button
              key={p.memory_id || i}
              onClick={() => onNavigate(`/memories/${p.memory_id}`)}
              className="aspect-square bg-[#1C1C1C] overflow-hidden group"
              data-testid={`feed-loc-photo-${p.memory_id}`}
            >
              <img
                src={getImageUrl(p.thumbnail_url || p.preview_url || p.url)}
                alt={p.title || ''}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                loading="lazy"
              />
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

// ─── DAILY RESURFACE CARD ─────────────────────────────
function DailyResurfaceCard({ data, onNavigate }) {
  const memories = data?.memories || [];
  const prompt = data?.prompt || 'A moment worth revisiting';
  if (!memories.length) return null;
  const hero = memories[0];
  return (
    <>
      <div className="px-4 pt-4 pb-2">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-rose-400 to-amber-400 flex items-center justify-center flex-shrink-0">
            <Sunrise className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading font-semibold text-white text-sm">Today's Memory</h3>
            <p className="text-stone-400 text-xs font-body italic truncate">{prompt}</p>
          </div>
        </div>
      </div>
      <button
        onClick={() => onNavigate(`/memories/${hero.memory_id}`)}
        className="relative w-full aspect-[16/9] overflow-hidden group"
        data-testid={`daily-resurface-hero-${hero.memory_id}`}
      >
        <img
          src={getImageUrl(hero.preview_url || hero.thumbnail_url || hero.url)}
          alt={hero.title || 'Memory'}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <p className="text-white/90 text-xs font-body">{hero.date?.slice(0, 10)}{hero.location ? ` \u2022 ${hero.location}` : ''}</p>
          {hero.title && <p className="text-white font-heading font-semibold text-base mt-0.5">{hero.title}</p>}
          {hero.ai_caption && <p className="text-white/70 text-xs mt-1 line-clamp-2 font-body">{hero.ai_caption}</p>}
        </div>
      </button>
      {memories.length > 1 && (
        <div className="px-4 py-3 flex gap-2">
          {memories.slice(1, 4).map((m, i) => (
            <button key={m.memory_id || i} onClick={() => onNavigate(`/memories/${m.memory_id}`)} className="flex-1 aspect-square rounded-lg overflow-hidden bg-[#1C1C1C] group" data-testid={`daily-resurface-thumb-${m.memory_id}`}>
              <img src={getImageUrl(m.thumbnail_url || m.preview_url || m.url)} alt={m.title || ''} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
            </button>
          ))}
        </div>
      )}
    </>
  );
}

// ─── WEEKLY RECAP CARD ────────────────────────────────
function WeeklyRecapCard({ data, onNavigate }) {
  const stats = data?.stats || {};
  const topPeople = data?.top_people || [];
  const topPlaces = data?.top_places || [];
  return (
    <>
      <div className="px-4 pt-4 pb-2">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center flex-shrink-0">
            <CalendarDays className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-heading font-semibold text-white text-sm">Weekly Recap</h3>
            <p className="text-stone-400 text-xs font-body">{data?.week_start} \u2014 {data?.week_end}</p>
          </div>
          <button onClick={() => onNavigate('/weekly-recap')} className="text-xs text-violet-600 font-medium hover:underline" data-testid="weekly-recap-see-all">See All</button>
        </div>
      </div>
      <div className="px-4 pb-4">
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="text-center p-3 rounded-xl bg-violet-50/60">
            <Camera className="w-4 h-4 text-violet-500 mx-auto mb-1" />
            <p className="text-lg font-bold text-white">{stats.photos_added || 0}</p>
            <p className="text-[10px] text-stone-400">Photos Added</p>
          </div>
          <div className="text-center p-3 rounded-xl bg-rose-50/60">
            <Users className="w-4 h-4 text-rose-500 mx-auto mb-1" />
            <p className="text-lg font-bold text-white">{stats.people_count || 0}</p>
            <p className="text-[10px] text-stone-400">People</p>
          </div>
          <div className="text-center p-3 rounded-xl bg-tan-50/60">
            <MapPin className="w-4 h-4 text-tan-500 mx-auto mb-1" />
            <p className="text-lg font-bold text-white">{stats.places_count || 0}</p>
            <p className="text-[10px] text-stone-400">Places</p>
          </div>
        </div>
        {topPeople.length > 0 && (
          <div className="mb-2">
            <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-wider mb-1.5">Top People</p>
            <div className="flex flex-wrap gap-1.5">
              {topPeople.map((p, i) => (
                <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#1C1C1C] text-xs text-stone-200">
                  <Users className="w-3 h-3" />{p.name} ({p.count})
                </span>
              ))}
            </div>
          </div>
        )}
        {topPlaces.length > 0 && (
          <div>
            <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-wider mb-1.5">Top Places</p>
            <div className="flex flex-wrap gap-1.5">
              {topPlaces.map((p, i) => (
                <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#1C1C1C] text-xs text-stone-200">
                  <MapPin className="w-3 h-3" />{p.location} ({p.count})
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// ─── CARD TYPE MAP ──────────────────────────────────────
const cardTypeConfig = {
  memory: {
    Component: MemoryCard,
    borderAccent: ''
  },
  on_this_day: {
    Component: OnThisDayCard,
    borderAccent: 'border-l-2 border-l-primary/20'
  },
  ai_highlight: {
    Component: AIHighlightCard,
    borderAccent: 'border-l-2 border-l-amber-300'
  },
  life_chapter: {
    Component: LifeChapterCard,
    borderAccent: 'border-l-2 border-l-primary/30'
  },
  people_memory: {
    Component: PeopleMemoryCard,
    borderAccent: 'border-l-2 border-l-rose-300'
  },
  location_memory: {
    Component: LocationMemoryCard,
    borderAccent: 'border-l-2 border-l-blue-300'
  },
  daily_resurface: {
    Component: DailyResurfaceCard,
    borderAccent: 'border-l-2 border-l-amber-400'
  },
  weekly_recap: {
    Component: WeeklyRecapCard,
    borderAccent: 'border-l-2 border-l-violet-400'
  }
};

// ─── MAIN EXPORT (memoized to prevent rerender on parent state change) ─
const FeedCard = React.memo(function FeedCard({ item, index = 0, onNavigate }) {
  const config = cardTypeConfig[item.type];
  if (!config) return null;

  const { Component, borderAccent } = config;

  return (
    <CardShell
      index={index}
      className={borderAccent}
      testId={`feed-${item.type}-${item.data?.memory_id || item.data?.chapter_id || item.data?.person_id || item.data?.location || item.id}`}
    >
      <Component data={item.data} onNavigate={onNavigate} />
    </CardShell>
  );
});

export default FeedCard;
